<template>
  <v-dialog
    :value="value"
    persistent
    max-width="34rem"
    content-class="customer-advance-dialog customer-advance-list-dialog"
    @keydown.esc="cancel"
  >
    <v-card class="customer-advance-card">
      <v-toolbar flat>
        <v-toolbar-title>{{ title }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :disabled="loading" title="Close" @click.prevent="cancel">
          <v-icon class="heading grey--text text--darken-4">close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <div class="fonthead pb-3">{{ message }}</div>

        <div
          v-for="(row, index) in visibleRows"
          :key="row.id"
          class="customer-advance-row"
        >
          <div class="customer-advance-row__job">
            <span class="customer-advance-row__id">{{ row.title }}</span>
            <span v-if="row.subtitle" class="customer-advance-row__sub">{{
              row.subtitle
            }}</span>
          </div>
          <CustomerAdvanceAmountField
            :value="amounts[row.id] || ''"
            :label="label"
            :error="errors[row.id] || ''"
            :max-amount="row.maxAmount"
            :max-label="maxLabel"
            :currency="row.currency"
            :disabled="loading"
            @input="onAmountInput(row.id, $event)"
            @enter="confirm"
          />
          <div
            v-if="index < visibleRows.length - 1"
            class="customer-advance-row__rule"
          ></div>
        </div>

        <div v-if="fetching" class="customer-advance-empty">Loading...</div>
        <div v-else-if="error" class="customer-advance-error">{{ error }}</div>
        <div v-else-if="!visibleRows.length" class="customer-advance-empty">
          None of the selected jobs can take a customer advance.
        </div>
        <div v-else class="customer-advance-hint pt-2">{{ hint }}</div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          class="button-width oms-dialog-action--secondary oms-btn oms-btn--secondary"
          :disabled="loading"
          @click.native="skip"
          >{{ skipText }}</v-btn
        >
        <v-btn
          class="white--text button-width oms-dialog-action--primary oms-btn oms-btn--primary"
          :loading="loading"
          :disabled="loading"
          @click.native="confirm"
          >{{ confirmText }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { StorageKeys } from "@/constants/constants";
import { customerAdvance } from "@/constants/api-urls";
import {
  apiErrorMessage,
  limitToTwoDecimals,
  validateAmount,
} from "@/utils/customerAdvance";
import CustomerAdvanceAmountField from "@/components/Common/CustomerAdvanceAmountField";
export default {
  name: "CustomerAdvanceListPopup",
  components: { CustomerAdvanceAmountField },
  props: {
    /* v-model, controls visibility */
    value: {
      type: Boolean,
      default: false,
    },
    /* One entry per selected job: { id, title, subtitle, currency }. The cap
       is not taken from here, it comes back with the saved advance. */
    rows: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: "Customer Advance",
    },
    message: {
      type: String,
      default:
        "Enter the customer advance for each job, or skip to carry on without one.",
    },
    label: {
      type: String,
      default: "Advance Amount",
    },
    hint: {
      type: String,
      default: "Leave a job blank if it has no advance.",
    },
    maxLabel: {
      type: String,
      default: "Customer Amount",
    },
    skipText: {
      type: String,
      default: "Skip",
    },
    confirmText: {
      type: String,
      default: "Continue",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      amounts: {},
      errors: {},
      fetching: false,
      error: "",
      /* Only the jobs the API returned. A selected job missing from the
         response is not UAE, so it cannot carry an advance. */
      visibleRows: [],
    };
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.reset();
        this.fetchSavedAdvances();
      }
    },
  },
  methods: {
    reset() {
      this.amounts = {};
      this.errors = {};
      this.error = "";
      this.visibleRows = [];
    },

    onAmountInput(id, value) {
      this.$set(this.amounts, id, value);
      this.$set(this.errors, id, "");
    },

    /* One request for every selected job. The response carries each job's
       saved advance and its live invoice amount, and leaves out any job that
       cannot take an advance. */
    fetchSavedAdvances() {
      if (!this.rows.length) return;
      this.fetching = true;
      const token = localStorage.getItem(StorageKeys.SessionKey);
      const byId = {};
      this.rows.forEach((row) => {
        byId[row.id] = row;
      });
      this.axios
        .get(`${this.constants.apiUrl}${customerAdvance}`, {
          headers: { authorization: `bearer ${token}` },
          params: {
            type: "job",
            jobIds: this.rows.map((row) => row.id).join(","),
          },
        })
        .then(
          (response) => {
            this.fetching = false;
            const body = response.data || {};
            const records = Array.isArray(body) ? body : body.data || [];
            this.visibleRows = records
              .filter((record) => record && byId[record.jobId])
              .map((record) => {
                const row = byId[record.jobId];
                const saved = record.customerAdvance;
                if (saved !== null && saved !== undefined && saved !== "") {
                  this.$set(
                    this.amounts,
                    row.id,
                    limitToTwoDecimals(saved)
                  );
                }
                return { ...row, maxAmount: record.customerAmount };
              });
          },
          (error) => {
            this.fetching = false;
            this.error = apiErrorMessage(
              error,
              "Could not load the saved customer advances."
            );
          }
        );
    },

    /* Every entered amount has to be valid, blank rows are simply left out.
       Zero is kept: it is how an existing advance gets cleared. */
    confirm() {
      const advances = [];
      const errors = {};
      let hasError = false;
      this.visibleRows.forEach((row) => {
        const value = String(this.amounts[row.id] || "").trim();
        if (!value) return;
        const message = validateAmount({
          value,
          max: row.maxAmount,
          maxLabel: this.maxLabel,
          currency: row.currency,
          required: false,
        });
        if (message) {
          errors[row.id] = message;
          hasError = true;
          return;
        }
        advances.push({ id: row.id, amount: Number(value) });
      });
      this.errors = errors;
      if (hasError) return;
      this.$emit("input", false);
      this.$emit("confirm", { advances });
    },

    skip() {
      this.$emit("input", false);
      this.$emit("confirm", { advances: [] });
    },

    cancel() {
      this.$emit("input", false);
      this.$emit("cancel");
    },
  },
};
</script>

<style scoped>
.customer-advance-row {
  padding-bottom: 0.25rem;
}
.customer-advance-row__job {
  align-items: baseline;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  padding-bottom: 0.125rem;
}
.customer-advance-row__id {
  color: var(--oms-ink-strong, #1a1916);
  font-size: 0.8125rem;
  font-weight: 700;
}
.customer-advance-row__sub {
  color: #94a3b8;
  font-size: 0.6875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.customer-advance-row__rule {
  border-top: 0.0625rem solid var(--oms-border, #ebe9e2);
  margin: 0.875rem 0 0.75rem;
}
.customer-advance-hint {
  color: #94a3b8;
  font-size: 0.6875rem;
}
.customer-advance-empty {
  color: #94a3b8;
  font-size: 0.8125rem;
  padding: 0.5rem 0;
}
.customer-advance-error {
  color: var(--oms-red, #ff0000);
  font-size: 0.7875rem;
  font-weight: 700;
  padding: 0.5rem 0;
}
</style>
